import { format } from "date-fns";

export const PAID_LEAVES_PER_MONTH = 1.5;

export type PayType = "Paid" | "Unpaid" | "Partial";

export interface LeaveLike {
  start_date: string | Date;
  is_half_day?: boolean | null;
  status?: string | null;
  pay_type?: string | null;
}

export interface SelectedDateLike {
  date: Date;
  isHalfDay: boolean;
}

export interface LeaveRow {
  date: Date;
  isHalfDay: boolean;
  payType: PayType;
  paidDays: number;
  unpaidDays: number;
}

const monthKey = (d: Date) => format(d, "yyyy-MM");
const dayValue = (isHalf?: boolean | null) => (isHalf ? 0.5 : 1);

/** Ek leave ka paid hissa. Unpaid = 0, Partial = 0.5 */
export const paidPart = (l: LeaveLike) => {
  const days = l.is_half_day ? 0.5 : 1;
  if (l.pay_type === "Unpaid") return 0;
  if (l.pay_type === "Partial") return 0.5;
  return days; // Paid ya purana record
};
/** Month-wise paid use (Approved + Pending) */
export const getPaidUsedByMonth = (existing: LeaveLike[]) => {
  const used: Record<string, number> = {};
  existing
    .filter((l) => l.status === "Approved" || l.status === "Pending")
    .forEach((l) => {
      const key = monthKey(new Date(l.start_date));
      used[key] = (used[key] || 0) + paidPart(l);
    });
  return used;
};

/** Har selected date = ek row, paid pehle phir unpaid */
export const buildLeaveRows = (
  selectedDates: SelectedDateLike[],
  existing: LeaveLike[]
): LeaveRow[] => {
  const used = { ...getPaidUsedByMonth(existing) };
  const sorted = [...selectedDates].sort((a, b) => a.date.getTime() - b.date.getTime());

  return sorted.map((sd) => {
    const key = monthKey(sd.date);
    const days = dayValue(sd.isHalfDay);
    const remaining = Math.max(0, PAID_LEAVES_PER_MONTH - (used[key] || 0));
    const paidDays = Math.min(days, remaining);
    const unpaidDays = days - paidDays;

    used[key] = (used[key] || 0) + paidDays;

    const payType: PayType =
      unpaidDays === 0 ? "Paid" : paidDays === 0 ? "Unpaid" : "Partial";

    return { date: sd.date, isHalfDay: sd.isHalfDay, payType, paidDays, unpaidDays };
  });
};

export const summarizeRows = (rows: LeaveRow[]) => {
  const totalPaid = rows.reduce((s, r) => s + r.paidDays, 0);
  const totalUnpaid = rows.reduce((s, r) => s + r.unpaidDays, 0);
  return { totalDays: totalPaid + totalUnpaid, totalPaid, totalUnpaid };
};


/** Balance = accrued leaves - eligible paid leaves used */

export const getAvailableBalance = (
    existing: LeaveLike[],
    today = new Date(),
    dateOfJoining?: string | Date
) => {
    if (!dateOfJoining) {
        return 0;
    }

    const joiningDate = new Date(dateOfJoining);

    if (
        Number.isNaN(joiningDate.getTime()) ||
        joiningDate > today
    ) {
        return 0;
    }

    const joiningYear = joiningDate.getFullYear();
    const joiningMonth = joiningDate.getMonth();
    const joiningDay = joiningDate.getDate();

    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();

    // Accrual starts from joining month in the joining year.
    // For employees who joined in a previous year, start in January.
    // If joined in the current year after the 15th, the joining month
    // is skipped and accrual starts from the next month.
    let startMonth = 0;

    if (joiningYear === currentYear) {
        startMonth = joiningDay <= 15 ? joiningMonth : joiningMonth + 1;
    } else if (joiningYear > currentYear) {
        return 0;
    }

    const accruedMonths = Math.max(0, currentMonth - startMonth + 1);
    const accrued = accruedMonths * PAID_LEAVES_PER_MONTH;

    // Deduct only Approved/Pending paid leave days
    // whose leave date is in the current month or an earlier month.
    const usedPaid = existing
        .filter((leave) => {
            if (
                leave.status !== "Approved" &&
                leave.status !== "Pending"
            ) {
                return false;
            }

            if (!leave.start_date) {
                return false;
            }

            const leaveDate = new Date(leave.start_date);

            if (Number.isNaN(leaveDate.getTime())) {
                return false;
            }

            // Future month leaves must not reduce today's balance.
            const isFutureMonth =
                leaveDate.getFullYear() > currentYear ||
                (
                    leaveDate.getFullYear() === currentYear &&
                    leaveDate.getMonth() > currentMonth
                );

            if (isFutureMonth) {
                return false;
            }

            // Unpaid leaves are not deducted.
            return leave.pay_type !== "Unpaid";
        })
        .reduce((sum, leave) => sum + paidPart(leave), 0);

    return Math.max(0, accrued - usedPaid);
};